import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";

import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

import WaveSurfer from "wavesurfer.js";
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline.esm.js'
import Minimap from 'wavesurfer.js/dist/plugins/minimap.esm.js'
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.esm.js'

import {
  setSelectedPlayTime
} from '@modules/media';

import {
  setChangedTimelineSubtitle,
  updateSubtitle
} from '@modules/subtitle';
import api from '@utils/axiosInstance';

function WaveSurferTimeline({ zoomLevel, videoDurationTime }) {

  const dispatch = useDispatch();
  const wavesurfer = useRef(null);
  const waveformRef = useRef(null);
  const wsRegionsRef = useRef(null);
  const minimapRef = useRef(null);
  const { uuid, lang } = useParams();

  // 리액트 상태로 activeRegion 관리
  const [activeRegion, setActiveRegion] = useState(null);

  // 기본 색상과 활성화 색상 정의
  const DEFAULT_REGION_COLOR = "rgba(211, 211, 211, 0.7)";
  const ACTIVE_REGION_COLOR = "rgba(0, 123, 255, 0.3)"; // 활성화된 region의 색상(빨간색 계열)

  const changedSubtitle = useSelector(state => state.subtitle.changedSubtitle);
  const subtitleList = useSelector(state => state.subtitle.subtitleList);
  const playTime = useSelector(state => state.media.playTime);
  const organizationData = useSelector(state => state.organization.organizationData);
  const userToken = useSelector(state => state.organization.userToken);
  const cdn = useSelector(state => state.organization.cdn);

  const formWaveSurferOptions = () => ({
    container: waveformRef.current,
    waveColor: "#eee",
    progressColor: "#99ccff",
    cursorColor: "#4b89dc",
    width: waveformRef.current ? waveformRef.current.clientWidth : 800,
    height: 150,
    minPxPerSec: zoomLevel,
    dragToSeek: true,
    scrollParent: false,
    plugins: [
      TimelinePlugin.create()
    ],
  });

  // WaveSurfer initialization
  useEffect(() => {
    // Initialize WaveSurfer only when videoDurationTime is available
    if (videoDurationTime > 0 && waveformRef.current) {
      console.log("Initializing WaveSurfer with videoDurationTime:", videoDurationTime);
      fetchPeaks();
    } else {
      console.log("Waiting for videoDurationTime...");
    }

    const handleResize = () => {
      if (!waveformRef.current) return;

      // 현재 peaks와 currentTime 저장
      const currentPeaks = wavesurfer.current.options.peaks;
      const currentTime = wavesurfer.current.getCurrentTime?.();

      // destroy 후 재생성
      wavesurfer.current.destroy();

      const newOptions = formWaveSurferOptions();
      newOptions.peaks = currentPeaks;
      newOptions.duration = videoDurationTime;

      wavesurfer.current = WaveSurfer.create(newOptions);
      wsRegionsRef.current = wavesurfer.current.registerPlugin(RegionsPlugin.create());

      // 이전 위치로 시크바 이동
      if (currentTime) {
        wavesurfer.current.seekTo(currentTime / videoDurationTime);
      }

      setupWaveSurferEvents();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [videoDurationTime]);

  // WaveSurfer event setup function
  const setupWaveSurferEvents = () => {
    wavesurfer.current.on("ready", () => {
      console.log("WaveSurfer is ready.");
      //wavesurfer.current.zoom(zoomLevel);
      const scrollElement = wavesurfer.current.renderer.scrollContainer;
      scrollElement.addEventListener("wheel", handleWheel, { passive: false });

      function handleWheel(event) {
        event.preventDefault();
        event.stopPropagation();
        scrollElement.scrollLeft += event.deltaY;
      }

      // WaveSurfer가 준비되었음을 표시
      wavesurfer.current.isReady = true;

      // WaveSurfer가 준비되면 regions 다시 업데이트
      if (subtitleList?.length > 0) {
        const regions = wsRegionsRef.current.getRegions();
        if (Object.keys(regions).length === 0) {
          // 기존 리전이 없을 때만 추가
          subtitleList.forEach(el => {
            const region = wsRegionsRef.current.addRegion({
              id: el.id,
              start: el.start,
              end: el.end,
              content: el.content,
              contentEditable: true,
              color: DEFAULT_REGION_COLOR,
              drag: true,
              resize: true,
            });

            // WaveSurfer가 자동으로 생성한 ID를 원래 ID로 강제 변경
            if (el.id === 0 && region.id !== 0) {
              region.id = 0;
            }
          });
        }
      }
    });

    wavesurfer.current.on("click", (x, y) => {
      console.log("WaveSurfer clicked at:", x, y);
      dispatch(setSelectedPlayTime(x * videoDurationTime));
    });

    setupRegionEvents();
    setupMinimapEvents();
  };

  // Region (subtitle) event setup function
  const setupRegionEvents = () => {
    wsRegionsRef.current.on("region-in", (region) => {
      console.log("Region entered:", region);
      setActiveRegion(region.id);
      // 활성화된 region 색상 변경
      region.setOptions({ color: ACTIVE_REGION_COLOR });
    });

    wsRegionsRef.current.on('region-out', (region) => {
      console.log('region-out', region);
      if (activeRegion === region.id) {
        setActiveRegion(null);
        // region을 벗어나면 기본 색상으로 복원
        region.setOptions({ color: DEFAULT_REGION_COLOR });
      }
    });

    wsRegionsRef.current.on('interaction', () => {
      setActiveRegion(null);
      wavesurfer.current.play();
    })

    wsRegionsRef.current.on("region-clicked", (region, e) => {
      console.log("Region clicked:", region);

      const regionIndex = subtitleList.findIndex(subtitle => subtitle.id === region.id);
      // contenteditable 요소인지 확인
      if (e.target.isContentEditable) {
        e.stopPropagation(); // 이벤트 전파 중단

        // 포커스 설정 및 커서 위치 지정
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(e.target);
        selection.removeAllRanges();
        selection.addRange(range);

        // 이벤트 리스너 설정
        e.target.addEventListener("input", handleInput);
        e.target.addEventListener("blur", handleBlur);
        e.target.addEventListener("click", handleContentClick); // 클릭 이벤트 추가

        return; // region 클릭 이벤트의 나머지 처리 중단
      } else {
        // contenteditable이 아닌 영역 클릭 시 기존 동작 수행
        dispatch(setSelectedPlayTime(region.start));
      }

      // contenteditable 클릭 이벤트 핸들러
      function handleContentClick(contentEvent) {
        contentEvent.stopPropagation(); // 이벤트 버블링 방지
      }

      function handleInput(e) {
        console.log("Region content input:", e.target.innerText);
        var changedObj = {
          id: subtitleList[regionIndex].id,
          start: region.start,
          end: region.end,
          content: region.content?.innerText || ""
        };
        dispatch(setChangedTimelineSubtitle(changedObj));
      }

      function handleBlur(e) {
        console.log("Region content editing completed.");
        fetchSubtitle();

        e.target.removeEventListener("input", handleInput);
        e.target.removeEventListener("blur", handleBlur);
        e.target.removeEventListener("click", handleContentClick); // 제거도 함께 해주기
      }
    });

    // Region 생성 시 contenteditable에 직접 이벤트 리스너 추가
    wsRegionsRef.current.on("region-created", (region) => {
      if (region.content && region.content.isContentEditable) {
        region.content.addEventListener("click", (e) => {
          e.stopPropagation();
        });
      }
    });

    wsRegionsRef.current.on("region-update", (region, side) => {
      const regions = wsRegionsRef.current.getRegions();
      const regionKeys = Object.keys(regions);

      let newStart = region.start;
      let newEnd = region.end;
      const originalLength = region.end - region.start;

      for (const key of regionKeys) {
        const currentRegion = regions[key];

        if (currentRegion.id !== region.id) {
          if (
            (newStart < currentRegion.end && newEnd > currentRegion.start) ||
            (newStart < currentRegion.start && newEnd > currentRegion.end)
          ) {
            if (newEnd > currentRegion.start && newStart < currentRegion.start) {
              newEnd = currentRegion.start;
              newStart = newEnd - originalLength;
            } else if (newStart < currentRegion.end && newEnd > currentRegion.end) {
              newStart = currentRegion.end;
              newEnd = newStart + originalLength;
            }

            region.setOptions({
              start: newStart,
              end: newEnd
            });
          }
        }
      }
    });

    wsRegionsRef.current.on("region-updated", (region) => {
      console.log("Region updated:", region);
      const regionsList = Object.values(wsRegionsRef.current.getRegions());
      const regionIndex = regionsList.findIndex(r => r.id === region.id);

      if (regionIndex !== -1) {
        var changedObj = {
          id: region.id,
          start: region.start,
          end: region.end,
          content: region.content?.innerText || ""
        };

        dispatch(setChangedTimelineSubtitle(changedObj));
        fetchSubtitle(regionIndex, changedObj);
        dispatch(updateSubtitle(regionIndex, changedObj));
      } else {
        console.error("Region not found:", region.id);
      }
    });
  };

  // Minimap 이벤트 설정
  const setupMinimapEvents = () => {
    if (!minimapRef.current) {
      console.warn('Minimap is not initialized yet');
      return;
    }

    minimapRef.current.on('click', (e) => {
      const clickTime = e * videoDurationTime;
      wavesurfer.current.seekTo(clickTime);
      dispatch(setSelectedPlayTime(clickTime));
    });
  };

  //fetchPeaks 함수 수정
  const fetchPeaks = async () => {
    let peaksData;
    try {
      const response = await fetch(`${cdn}/${uuid}/extData/${uuid}.peaks`);
      peaksData = await response.json();
    } catch (error) {
      const samplesPerSecond = 100; // WaveSurfer 기본값과 일치
      const totalSamples = Math.ceil(videoDurationTime * samplesPerSecond);

      // 랜덤한 파형 생성 (더 자연스러운 파형)
      peaksData = new Array(totalSamples);
      for (let i = 0; i < totalSamples; i++) {
        // 약간의 변동이 있는 랜덤 값 (-0.3 ~ 0.3 범위)
        const value = (Math.random() * 0.6) - 0.3;
        peaksData[i] = value;
      }
    }

    console.log("Final peaks data length:", peaksData.length);
    await initializeWaveSurfer(peaksData);
  };

  // Initialize WaveSurfer
  const initializeWaveSurfer = async (peaksData) => {
    if (wavesurfer.current) {
      wavesurfer.current.destroy();
    }

    console.log("Creating WaveSurfer instance...");
    const waveSurferOptions = formWaveSurferOptions(waveformRef.current);
    waveSurferOptions.peaks = peaksData;
    waveSurferOptions.duration = videoDurationTime;
    wavesurfer.current = WaveSurfer.create(waveSurferOptions);
    wsRegionsRef.current = wavesurfer.current.registerPlugin(RegionsPlugin.create());
    minimapRef.current = wavesurfer.current.registerPlugin(Minimap.create({
      height: 20,
      waveColor: '#ddd',
      progressColor: '#999',
    }));

    setupWaveSurferEvents();
  };

  // Region 업데이트 로직을 useCallback으로 최적화
  const updateRegions = useCallback(() => {
    if (!wsRegionsRef.current || !wavesurfer.current) {
      console.log("WaveSurfer or Regions not initialized yet.");
      return;
    }

    //console.log("Updating regions with subtitleList:", subtitleList);
    const regions = wsRegionsRef.current.getRegions();

    // Remove all existing regions
    Object.keys(regions).forEach(id => {
      regions[id].remove();
    });

    // Add new subtitles
    subtitleList.forEach(el => {
      const region = wsRegionsRef.current.addRegion({
        id: el.id,
        start: el.start,
        end: el.end,
        content: el.content,
        contentEditable: true,
        color: DEFAULT_REGION_COLOR,
        drag: true,
        resize: true,
      });

      // WaveSurfer가 자동으로 생성한 ID를 원래 ID로 강제 변경
      if (el.id === 0 && region.id !== 0) {
        region.id = 0;
      }

      // Region이 생성된 후 content 요소에 직접 이벤트 처리 추가
      if (region.element) {
        const contentElement = region.element.querySelector('[contenteditable="true"]');
        if (contentElement) {
          contentElement.addEventListener("click", (e) => {
            e.stopPropagation();
            // 텍스트 편집에 포커스
            const selection = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(contentElement);
            selection?.removeAllRanges();
            selection?.addRange(range);
          });
        }
      }
    });
  }, [subtitleList]);

  // subtitleList 변경 시 region 업데이트
  useEffect(() => {
    if (subtitleList?.length > 0 && wavesurfer.current && wsRegionsRef.current && wavesurfer.current.isReady) {
      updateRegions();
    }
  }, [subtitleList, updateRegions]);

  function fetchSubtitle() {
    var tmpSubList = wsRegionsRef.current.getRegions();
    // getRegions()는 객체를 반환하므로 Object.values로 배열로 변환
    var tmpParsed = Object.values(tmpSubList).map(list => ({
      identifier: String(list.id),
      start: list.start,
      end: list.end,
      text: list.content?.innerText || '',
      styles: ""
    }));

    api({
      method: 'post',
      url: '/subtitle/aUpdate',
      data: {
        tk: userToken,
        uuid: uuid,
        ogc: organizationData.organization_code,
        lang: lang,
        subtitle: tmpParsed
      }
    })
      .then((response) => {
        var data = response.data.data;
        console.log('edit data', data)
        //var langData = data.subtitle;
      })
      .catch((error) => {
        console.log(error);
      });
  }

  // Update timeline when subtitle changes
  useEffect(() => {
    if (changedSubtitle && wsRegionsRef.current) {
      const regions = wsRegionsRef.current.getRegions();
      var selectedRegion = regions[changedSubtitle.idx];
      console.log("Changed subtitle:", changedSubtitle, selectedRegion);
      if (selectedRegion) {
        selectedRegion.setOptions({
          content: changedSubtitle.content,
          color: ACTIVE_REGION_COLOR // 활성화된 region 색상으로 변경
        });
      }
      changedSubtitle.type == 'end' ? wavesurfer.current?.setScrollTime((changedSubtitle.start + changedSubtitle.end) / 2) : wavesurfer.current?.setScrollTime(changedSubtitle.start);
    }
  }, [changedSubtitle?.content, wsRegionsRef.current]);

  useEffect(() => {
    if (wavesurfer.current && waveformRef.current) {
      wavesurfer.current.zoom(zoomLevel);
    }
  }, [zoomLevel]);

  //Update regions color based on playTime
  useEffect(() => {
    if (!wsRegionsRef.current || !wavesurfer.current || !wavesurfer.current.isReady) {
      return;
    }

    const regions = wsRegionsRef.current.getRegions();

    // 모든 region 확인하여 색상 업데이트
    Object.values(regions).forEach(region => {
      const isActive = playTime >= region.start && playTime <= region.end;
      const currentColor = region.color;
      const newColor = isActive ? ACTIVE_REGION_COLOR : DEFAULT_REGION_COLOR;

      // 색상이 변경되어야 할 때만 setOptions 호출
      if (currentColor !== newColor) {
        region.setOptions({ color: newColor });
      }
    });

    const waveCurrentTime = wavesurfer.current.getCurrentTime();
    if (Math.abs(playTime - waveCurrentTime) > 0.1) {
      wavesurfer.current.seekTo(playTime / videoDurationTime);
    }
  }, [playTime, wsRegionsRef.current]);

  return (
    <div id="waveform" ref={waveformRef} />
  );
}

export default WaveSurferTimeline;